import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@heroui/react';
import { Tooltip } from '@/components/Tooltip';
import {
  CheckIcon,
  PaperclipIcon,
  PlusIcon,
  PollIcon,
  SmileIcon,
} from '@/components/icons';

/**
 * Composer attach-menu. Single "+" trigger that opens a popover with
 * the file picker + poll creator (and future: GIF, location, etc).
 *
 * Why over a row of icons: poll/file are infrequent enough that a
 * dedicated icon-per-kind crowded the composer rail. Voice messages
 * stay separate (frequent, single tap), but everything that's an
 * "attachment kind" lives behind +. Telegram + Slack ship the same.
 */
export function AttachMenu(props: {
  onPickFile: () => void;
  onCreatePoll: () => void;
  onCreateTasks: () => void;
  /** Emoji entry — shown only on mobile, where the standalone smile
   *  button is hidden to free composer-row width. */
  onOpenEmojiMobile?: () => void;
  /** Formatting toggle — shown only on mobile (same reason). Boolean to
   *  reflect the current state in the entry copy. */
  formatOpenMobile?: boolean;
  onToggleFormatMobile?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // No GIF picker on the web (the Tenor CDN is unreliable on RF networks);
  // a dropped .gif goes through the normal attachment pipeline and animates.
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Tooltip label={t('attach.attach')}>
        <Popover.Trigger
          aria-label={t('attach.attach')}
          className="transition-colors hover:text-foreground hover:bg-default/50 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-dim"
        >
          <PlusIcon className="h-[22px] w-[22px] icon-bounce-hover" />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content className="w-72 overflow-hidden" placement="top start">
        <Popover.Dialog className="p-1.5">
        <AttachItem
          icon={<PaperclipIcon className="h-[18px] w-[18px]" />}
          label={t('attach.file')}
          description={t('attach.fileDesc')}
          onClick={() => {
            setOpen(false);
            props.onPickFile();
          }}
        />
        <AttachItem
          icon={<PollIcon className="h-[18px] w-[18px]" />}
          label={t('attach.poll')}
          description={t('attach.pollDesc')}
          onClick={() => {
            setOpen(false);
            props.onCreatePoll();
          }}
        />
        <AttachItem
          icon={<CheckIcon className="h-[18px] w-[18px]" />}
          label={t('attach.tasks')}
          description={t('attach.tasksDesc')}
          onClick={() => {
            setOpen(false);
            props.onCreateTasks();
          }}
        />
        {/* Mobile-only entries — emoji + format toggle. Hidden at md+
            because they have dedicated icons in the composer row there. */}
        {props.onOpenEmojiMobile && (
          <div className="md:hidden">
            <AttachItem
              icon={<SmileIcon className="h-[18px] w-[18px]" />}
              label={t('composer.emoji')}
              description={t('attach.emojiDesc')}
              onClick={() => {
                setOpen(false);
                props.onOpenEmojiMobile?.();
              }}
            />
          </div>
        )}
        {props.onToggleFormatMobile && (
          <div className="md:hidden">
            <AttachItem
              icon={
                <span className="font-mono text-[12px] font-semibold tracking-[-0.02em]">
                  Aa
                </span>
              }
              label={props.formatOpenMobile ? t('attach.hideFormat') : t('composer.formatting')}
              description={t('attach.formatDesc')}
              onClick={() => {
                setOpen(false);
                props.onToggleFormatMobile?.();
              }}
            />
          </div>
        )}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

function AttachItem(props: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick: () => void;
}) {
  // One neutral tile for every option; theme tokens work in light mode too.
  const tile = 'bg-default/60 text-ink-dim group-hover/att:bg-default group-hover/att:text-ink';
  return (
    <button
      type="button"
      role="menuitem"
      onClick={props.onClick}
      className="group/att mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-default/50"
    >
      <span
        className={
          'grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors ' +
          tile
        }
      >
        {props.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-light tracking-[-0.015em] text-ink">
          {props.label}
        </span>
        {props.description && (
          <span className="mt-0.5 block truncate text-[11px] leading-tight text-ink-muted">
            {props.description}
          </span>
        )}
      </span>
    </button>
  );
}
